import type {
  JournalEntryResponse,
  JournalStatus,
  SaveJournalRequest,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { ACCOUNTS_KEY } from '@/features/accounts/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * FIN-02 data layer. Every workflow mutation invalidates the journal keys AND
 * the accounts keys: posting/reversing moves balances the chart screen shows.
 */

export const JOURNALS_KEY = ['journals'] as const;

export interface JournalListFilters {
  status?: JournalStatus | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

/** FIN-02's register, one page at a time (NFR-42). All three filters run in SQL. */
export function useJournals(
  filters: JournalListFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...JOURNALS_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.journals.list({
        query: {
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.from ? { from: filters.from } : {}),
          ...(filters.to ? { to: filters.to } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useJournal(entryId: string | undefined) {
  return useQuery({
    queryKey: [...JOURNALS_KEY, 'one', entryId],
    enabled: Boolean(entryId),
    queryFn: async (): Promise<JournalEntryResponse> => {
      const response = await api.journals.get({ params: { entryId: entryId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: JOURNALS_KEY });
    void queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY });
  };
}

export function useCreateJournal() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: SaveJournalRequest): Promise<JournalEntryResponse> => {
      const response = await api.journals.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateJournal(entryId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: SaveJournalRequest): Promise<JournalEntryResponse> => {
      const response = await api.journals.update({ params: { entryId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteJournal() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (entryId: string): Promise<void> => {
      const response = await api.journals.remove({ params: { entryId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: invalidate,
  });
}

/** The four workflow verbs share one shape: POST /:id/<verb> → the entry. */
function useWorkflowVerb(
  verb: 'submit' | 'approve' | 'post',
): (entryId: string) => Promise<JournalEntryResponse> {
  return async (entryId: string) => {
    const response = await api.journals[verb]({ params: { entryId } });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  };
}

export function useSubmitJournal() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: useWorkflowVerb('submit'), onSuccess: invalidate });
}

export function useApproveJournal() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: useWorkflowVerb('approve'), onSuccess: invalidate });
}

export function useRejectJournal() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({
      entryId,
      reason,
    }: {
      entryId: string;
      reason: string;
    }): Promise<JournalEntryResponse> => {
      const response = await api.journals.reject({ params: { entryId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

export function usePostJournal() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: useWorkflowVerb('post'), onSuccess: invalidate });
}

/**
 * A23 — the reverse carries its typed reason, the shape `useReverseReceipt`
 * and `useReversePayment` already had.
 *
 * The argument is an OBJECT rather than a second positional parameter because
 * `useMutation` takes exactly one variable, and it is `{ entryId, reason }`
 * rather than a bare string for the reason the reject hook above is shaped the
 * same way: two strings in a row is a pair anybody can transpose, and
 * transposing these two sends the entry id to the server as an accountant's
 * explanation and posts nothing.
 */
export function useReverseJournal() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({
      entryId,
      reason,
    }: {
      entryId: string;
      reason: string;
    }): Promise<JournalEntryResponse> => {
      const response = await api.journals.reverse({ params: { entryId }, body: { reason } });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}
